import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef } from 'react';

import type {
  SnapshotEntityType,
  SnapshotSource,
} from '@superfunnel/types/snapshot';

import { useAnalytics } from '../contexts/AnalyticsContext';
import {
  createSnapshot,
  findWebsiteVariantId,
  parseWebsiteEntityPath,
} from '../services/SnapshotsService';

const IDLE_DELAY_MS = 20_000;
const MAX_WAIT_MS = 120_000;

interface PendingSnapshot {
  accountId: string;
  timer: ReturnType<typeof setTimeout>;
  firstScheduledAt: number;
}

const ENTITY_TYPE_BY_COLLECTION: Record<string, SnapshotEntityType> = {
  websites: 'website',
  forms: 'quizForm',
  globalSections: 'globalSection',
};

export const getEntityType = (entityPath: string) =>
  ENTITY_TYPE_BY_COLLECTION[entityPath.split('/').at(-2) ?? ''];

const pendingRestores = new Map<string, string>();

export const markPendingRestore = (entityPath: string, snapshotId: string) =>
  pendingRestores.set(entityPath, snapshotId);

export type CapturedSnapshotSource = Extract<
  SnapshotSource,
  'autosave' | 'agent' | 'restore'
>;

export function useSnapshotCapture() {
  const { track } = useAnalytics();
  const pending = useRef(new Map<string, PendingSnapshot>());

  const trackPageSnapshotCompleted = useCallback(
    async (
      accountId: string,
      entityPath: string,
      snapshotId: string,
      source: SnapshotSource,
    ) => {
      const website = parseWebsiteEntityPath(entityPath);
      if (!website) return;
      track('page_snapshot_completed', {
        page_id: website.dynamicPageId,
        variant_id: await findWebsiteVariantId(
          accountId,
          website.dynamicPageId,
          website.websiteId,
        ).catch(() => undefined),
        snapshot_id: snapshotId,
        source,
      });
    },
    [track],
  );

  const captureSnapshot = useCallback(
    async (
      accountId: string,
      entityPath: string,
      source: CapturedSnapshotSource,
    ) => {
      clearTimeout(pending.current.get(entityPath)?.timer);
      pending.current.delete(entityPath);
      const restoredSnapshotId =
        source === 'restore' ? pendingRestores.get(entityPath) : undefined;
      pendingRestores.delete(entityPath);

      try {
        const { snapshotId } = await createSnapshot(
          accountId,
          [entityPath],
          source,
          { restoredSnapshotId },
        );
        if (!snapshotId) return;
        track('snapshot_capture_completed', {
          snapshot_id: snapshotId,
          entity_type: getEntityType(entityPath),
          entity_path: entityPath,
          source,
        });
        await trackPageSnapshotCompleted(
          accountId,
          entityPath,
          snapshotId,
          source,
        );
      } catch (error) {
        Sentry.captureException(error, {
          tags: { area: 'snapshot-capture' },
          extra: { entityPath, source },
        });
        track('snapshot_capture_failed', {
          entity_type: getEntityType(entityPath),
          entity_path: entityPath,
          source,
        });
      }
    },
    [track, trackPageSnapshotCompleted],
  );

  const scheduleSnapshot = useCallback(
    (accountId: string, entityPath: string) => {
      const existing = pending.current.get(entityPath);
      clearTimeout(existing?.timer);

      const firstScheduledAt = existing?.firstScheduledAt ?? Date.now();
      const delay = Math.min(
        IDLE_DELAY_MS,
        firstScheduledAt + MAX_WAIT_MS - Date.now(),
      );

      pending.current.set(entityPath, {
        accountId,
        firstScheduledAt,
        timer: setTimeout(
          () => captureSnapshot(accountId, entityPath, 'autosave'),
          Math.max(delay, 0),
        ),
      });
    },
    [captureSnapshot],
  );

  const flushSnapshots = useCallback(() => {
    for (const [entityPath, { accountId }] of pending.current) {
      captureSnapshot(accountId, entityPath, 'autosave');
    }
  }, [captureSnapshot]);

  const flushSnapshotsRef = useRef(flushSnapshots);
  flushSnapshotsRef.current = flushSnapshots;

  useEffect(() => () => flushSnapshotsRef.current(), []);

  return { captureSnapshot, scheduleSnapshot, flushSnapshots };
}
